import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logUnresolvedS3Key, resolveDocumentS3Keys } from "@/lib/order-doc-delete";

/**
 * Best-effort: when an order's checked-in trailer is removed (or replaced),
 * delete from S3 the BoLs that were synced from that previous trailer's
 * latest check-in for this order, and remove the corresponding
 * `order_bol_images` rows.
 *
 * Failures surface as toast warnings only — they never block the order update.
 */
export async function removeTrailerBolFromOrder(params: {
  orderId: string;
  orderUniqueId: string;
  previousTrailerRowId: string;
  tenantId: string;
  tenantSlug: string | null | undefined;
}): Promise<void> {
  const { orderId, orderUniqueId, previousTrailerRowId, tenantId, tenantSlug } = params;
  if (!tenantSlug) return;

  try {
    // 1. Find linked BoL rows for this order that came from the previous trailer.
    const { data: linkedBols, error: bolErr } = await supabase
      .from("order_bol_images")
      .select("id, image_path, s3_key, file_name")
      .eq("order_id", orderId)
      .eq("tenant_id", tenantId)
      .eq("source_trailer_id", previousTrailerRowId);
    if (bolErr) {
      console.warn("removeTrailerBolFromOrder: lookup failed:", bolErr.message);
      toast.warning(`Trailer BoL S3 cleanup warning: ${bolErr.message}`);
      return;
    }
    if (!linkedBols || linkedBols.length === 0) return;


    // 2/3. Resolve each row's S3 key through the shared resolver (row `s3_key`
    //      first, then the sync log by ORIGINAL file name) and issue the delete.
    //      Unresolvable keys are logged as failures rather than skipped silently.
    let deleteErrors = 0;
    let unresolved = 0;
    for (const bol of linkedBols as any[]) {
      const keys = await resolveDocumentS3Keys(bol, orderUniqueId);
      if (keys.length === 0) {
        unresolved += 1;
        console.warn(
          `[bol-trailer-unsync] No S3 key resolved for order_bol_images ${bol.id} (order ${orderUniqueId})`,
        );
        await logUnresolvedS3Key({
          bucket: "bol-images",
          orderUniqueId,
          tenantId,
          sourcePath: bol.file_name ?? bol.image_path ?? null,
          context: "trailer BoL unsync",
        });
        continue;
      }
      for (const key of keys) {
        const { data, error } = await supabase.functions.invoke("sync-bol-s3", {
          body: {
            action: "delete",
            tenant_slug: tenantSlug,
            order_id: orderUniqueId,
            s3_key: key,
          },
        });
        if (error || (data && data.success === false)) {
          deleteErrors += 1;
          console.warn("sync-bol-s3 delete failed for", key, error?.message ?? data?.error);
        }
      }
    }

    // 4. Remove the linked rows from the order regardless of S3 outcome.
    const ids = linkedBols.map((b) => b.id);
    const { error: delErr } = await supabase
      .from("order_bol_images")
      .delete()
      .in("id", ids)
      .eq("tenant_id", tenantId);
    if (delErr) {
      console.warn("order_bol_images cleanup failed:", delErr.message);
      toast.warning(`Trailer BoL local cleanup warning: ${delErr.message}`);
    }

    if (deleteErrors > 0 || unresolved > 0) {
      const parts: string[] = [];
      if (deleteErrors > 0) parts.push(`${deleteErrors} S3 deletion${deleteErrors === 1 ? "" : "s"} failed`);
      if (unresolved > 0) parts.push(`${unresolved} with no resolvable S3 key`);
      toast.warning(`Trailer BoL cleanup: ${parts.join(", ")}. See console for details.`);
    } else if (ids.length > 0) {
      toast.success(`Removed ${ids.length} trailer BoL${ids.length === 1 ? "" : "s"} from order`);
    }
  } catch (err: any) {
    console.warn("removeTrailerBolFromOrder threw:", err);
    toast.warning(`Trailer BoL S3 cleanup warning: ${err?.message ?? "unknown error"}`);
  }
}